import { useCallback, useEffect, useRef, useState } from "react";
import { FolderOpen, Wand, Upload } from "@/icons/lucide-compat";
import { aspectRatioLabel, canvasSettingsForMedia } from "@openreel/core";
import { useProjectStore } from "../../stores/project-store";
import { useUIStore } from "../../stores/ui-store";
import { useAuth } from "../../services/auth";
import { TemplateGallery } from "./TemplateGallery";
import { RecentProjects } from "./RecentProjects";
import { useRouter } from "../../hooks/use-router";
import { useEditorPreload } from "../../hooks/useEditorPreload";
import { useAnalytics, AnalyticsEvents } from "../../hooks/useAnalytics";
import { AccountMenu } from "../AccountMenu";
import {
  FOCUS_RING,
  MUTED,
  PILL_BASE,
  PILL_DARK,
  PILL_DIMMED,
  PILL_LIGHT,
  PILL_MOTION,
  SECTION_HEAD,
  SURFACE_BG,
} from "../surface";

/**
 * The dashboard, on the same canvas as the onboarding.
 *
 * It used to be a themed sidebar app: a 228px rail, boxed cards, 13px copy.
 * Signing in therefore stepped from a wide near-black surface with one very
 * large light headline straight into a different-looking product. This is the
 * onboarding's own surface instead: fixed #0a0a0a, the masked artwork behind
 * the heading, borderless controls in white/6%, and the same Outfit extralight
 * headline. Nothing here reads from the editor's light/dark tokens.
 *
 * The sidebar is gone. Navigation moved into the header row, so the page is one
 * column and the artwork runs the full width behind it.
 */

/** The three surfaces the header switches between. */
type ViewMode = "home" | "projects" | "templates";

const NAV_ITEMS: Array<{ id: ViewMode; label: string }> = [
  { id: "home", label: "Home" },
  { id: "projects", label: "Projects" },
  { id: "templates", label: "Templates" },
];

/**
 * A strip heading with an optional way through to the full list. Both strips
 * on Home use it, so their type and rhythm cannot drift apart.
 */
const SectionHeader: React.FC<{
  title: string;
  action?: React.ReactNode;
}> = ({ title, action }) => (
  <div className="flex items-baseline justify-between gap-4">
    <h2 className={SECTION_HEAD}>{title}</h2>
    {action}
  </div>
);

interface WelcomeScreenProps {
  initialTab?: "templates" | "recent";
}

export const WelcomeScreen: React.FC<WelcomeScreenProps> = ({ initialTab }) => {
  const skipWelcomeScreen = useUIStore((state) => state.skipWelcomeScreen);
  const createNewProject = useProjectStore((state) => state.createNewProject);
  const importMedia = useProjectStore((state) => state.importMedia);
  const updateSettings = useProjectStore((state) => state.updateSettings);
  const { navigate } = useRouter();
  const { track } = useAnalytics();
  const { user } = useAuth();

  const [viewMode, setViewMode] = useState<ViewMode>(
    initialTab === "templates"
      ? "templates"
      : initialTab === "recent"
        ? "projects"
        : "home",
  );
  const [isDragOver, setIsDragOver] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEditorPreload(true);

  // A visitor who turned on "skip on startup" never wants this screen: honor it
  // by handing straight off to the editor, the same contract the old screen had.
  useEffect(() => {
    if (skipWelcomeScreen) {
      navigate("editor");
    }
  }, [skipWelcomeScreen, navigate]);

  const firstName = user?.firstName?.trim() || null;
  const greeting = firstName
    ? `What are we editing today, ${firstName}?`
    : "What are we editing today?";

  const handleProjectSelected = useCallback(() => {
    navigate("editor");
  }, [navigate]);

  const handleTemplateApplied = useCallback(() => {
    navigate("editor");
  }, [navigate]);

  /**
   * Start an edit by describing it.
   *
   * Text to video generation proper sits behind FEATURES.aiGenerate, which is
   * off, so this opens the Director instead: the assistant that is actually
   * wired up and can build and change the edit from a plain request. Pointing
   * the tile at a flagged-off surface would give it nothing to land on.
   */
  const handleGenerate = useCallback(() => {
    createNewProject("Untitled project", {
      width: 1920,
      height: 1080,
      frameRate: 30,
    });
    track(AnalyticsEvents.PROJECT_CREATED, {
      preset: "16:9",
      width: 1920,
      height: 1080,
      frameRate: 30,
      source: "home_generate",
    });
    const ui = useUIStore.getState();
    // Open the column, but do not pin Director into it. Director is already
    // what the column shows while nothing is selected, so uncollapsing is
    // enough to land the user in it. Pinning meant it never yielded: with
    // agentChat visible, showDirector stays true whatever the user selects, so
    // every clip they picked afterwards still showed Director instead of that
    // clip's inspector.
    ui.setDirectorCollapsed(false);
    navigate("editor");
  }, [createNewProject, navigate, track]);

  // Create a project, pull the dropped or picked files into its media library,
  // then open the editor. Same store calls the editor's own Assets panel uses
  // (createNewProject + importMedia), so nothing here is a parallel import path.
  const handleImportFiles = useCallback(
    async (fileList: FileList | File[]) => {
      const files = Array.from(fileList);
      if (files.length === 0 || isImporting) return;

      setIsImporting(true);
      try {
        // Landscape to start, then resized to the footage below. The clip has
        // not been probed yet at this point, so there is nothing better to
        // open with.
        createNewProject("Untitled project", {
          width: 1920,
          height: 1080,
          frameRate: 30,
        });

        const before =
          useProjectStore.getState().project.mediaLibrary.items.length;

        for (const file of files) {
          await importMedia(file);
        }

        // The canvas takes its shape from the footage rather than always
        // opening 16:9. Importing a phone clip used to land it pillarboxed
        // inside a landscape frame, so the first thing anyone had to do was go
        // and fix the ratio by hand. The dimensions are already rotation
        // corrected by the probe, so a clip filmed sideways comes out the way
        // it looks rather than the way it is stored.
        const imported = useProjectStore
          .getState()
          .project.mediaLibrary.items.slice(before);
        const shaper = imported.find(
          (item) => item.type !== "audio" && canvasSettingsForMedia(item.metadata),
        );
        const canvas = canvasSettingsForMedia(shaper?.metadata);

        if (canvas) {
          await updateSettings(canvas);
        }

        track(AnalyticsEvents.PROJECT_CREATED, {
          preset: aspectRatioLabel(
            canvas?.width ?? 1920,
            canvas?.height ?? 1080,
          ),
          width: canvas?.width ?? 1920,
          height: canvas?.height ?? 1080,
          frameRate: canvas?.frameRate ?? 30,
          source: "home_import",
        });

        navigate("editor");
      } finally {
        setIsImporting(false);
      }
    },
    [isImporting, createNewProject, importMedia, updateSettings, navigate, track],
  );

  const handleDrop = useCallback(
    (event: React.DragEvent) => {
      event.preventDefault();
      setIsDragOver(false);
      void handleImportFiles(event.dataTransfer.files);
    },
    [handleImportFiles],
  );

  const handleFileInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (event.target.files) {
        void handleImportFiles(event.target.files);
      }
      // Reset so picking the same file twice still fires a change.
      event.target.value = "";
    },
    [handleImportFiles],
  );

  const seeAll = (target: ViewMode, label: string) => (
    <button
      type="button"
      onClick={() => setViewMode(target)}
      className={`rounded-[6px] text-[14px] ${MUTED} transition-colors hover:text-white ${FOCUS_RING}`}
    >
      {label}
    </button>
  );

  const renderHome = () => (
    <>
      {/* The hero band is the drop target, not a single tile inside it. The
          old dropzone was one card in a row of three; this is the whole
          headline area, which is what people aim at anyway. */}
      <div
        onDrop={handleDrop}
        onDragOver={(event) => {
          event.preventDefault();
          setIsDragOver(true);
        }}
        onDragLeave={() => setIsDragOver(false)}
        className={`relative z-10 mx-auto w-full max-w-3xl rounded-[14px] px-6 pt-[10vh] text-center motion-safe:transition-[background-color] motion-safe:duration-200 ${
          isDragOver ? "bg-white/[0.04]" : ""
        }`}
      >
        <p className={`text-[14px] font-normal ${MUTED}`}>
          {isDragOver ? "Drop to start a project" : "Welcome back"}
        </p>
        <h1 className="mt-3 font-sans text-[clamp(2rem,4.5vw,3.25rem)] font-extralight leading-[1.08] tracking-[-0.02em] text-white">
          {greeting}
        </h1>
        <p className={`mt-4 text-[16px] ${MUTED}`}>
          Start something, or pick up where you left off.
        </p>

        {/* Three ways in, rather than one dropzone and a row of format chips.
            The format is a decision about the edit, not about starting one, and
            asking for it before there is anything on the timeline put a choice
            in front of people that the first clip answers by itself. */}
        <div className="mt-12 flex flex-wrap items-center justify-center gap-3">
          <button
            type="button"
            disabled={isImporting}
            onClick={() => fileInputRef.current?.click()}
            className={`${PILL_BASE} ${PILL_MOTION} ${FOCUS_RING} ${
              isImporting ? PILL_DIMMED : PILL_LIGHT
            }`}
          >
            <Upload size={16} strokeWidth={1.5} aria-hidden />
            {isImporting ? "Setting up your project..." : "Add video"}
          </button>
          <button
            type="button"
            onClick={handleGenerate}
            className={`${PILL_BASE} ${PILL_MOTION} ${FOCUS_RING} ${PILL_DARK}`}
          >
            <Wand size={16} strokeWidth={1.5} aria-hidden />
            Generate video
          </button>
          <button
            type="button"
            onClick={() => setViewMode("projects")}
            className={`${PILL_BASE} ${PILL_MOTION} ${FOCUS_RING} ${PILL_DARK}`}
          >
            <FolderOpen size={16} strokeWidth={1.5} aria-hidden />
            Edit existing project
          </button>
        </div>
      </div>

      {/* Two left aligned strips under the hero. They scroll up beneath the
          artwork, which stays put because the backdrop layer is fixed. */}
      <div className="relative z-10 mx-auto mt-24 w-full max-w-5xl px-6 pb-28 sm:px-8">
        {/* Templates sit on Home, not only behind their own tab. A first-time
            user lands here with nothing to open and no media imported yet, and
            a ready made scene is the fastest thing they can actually do. */}
        <SectionHeader
          title="Start from a template"
          action={seeAll("templates", "See all")}
        />
        <div className="mt-6">
          <TemplateGallery
            compact
            limit={4}
            hideHeader
            onTemplateApplied={handleTemplateApplied}
          />
        </div>

        <div className="mt-20">
          <SectionHeader
            title="Recent projects"
            action={seeAll("projects", "See all")}
          />
          <div className="mt-6">
            <RecentProjects
              limit={4}
              hideChrome
              onProjectSelected={handleProjectSelected}
            />
          </div>
        </div>

        <p className={`mt-16 text-[13px] ${MUTED}`}>
          Recent projects are stored locally in this browser.
        </p>
      </div>
    </>
  );

  const renderList = (
    title: string,
    blurb: string,
    body: React.ReactNode,
  ) => (
    <div className="relative z-10 mx-auto w-full max-w-5xl px-6 pb-28 pt-[8vh] sm:px-8">
      <h1 className="font-sans text-[clamp(1.75rem,3.5vw,2.5rem)] font-extralight leading-[1.1] tracking-[-0.02em] text-white">
        {title}
      </h1>
      <p className={`mt-3 text-[15px] ${MUTED}`}>{blurb}</p>
      <div className="mt-10">{body}</div>
    </div>
  );

  return (
    <div
      className={`fixed inset-0 z-50 flex flex-col overflow-y-auto text-white ${SURFACE_BG}`}
    >
      {/* The picture is anchored at the top on every view, behind the heading,
          and gone by the time the card grids start. It stays put while the
          content scrolls under it because the layer is fixed. */}
      <div className="art-backdrop art-backdrop--top" aria-hidden="true" />

      <header className="relative z-10 flex shrink-0 items-center justify-between gap-6 px-6 py-5 sm:px-8">
        <button
          type="button"
          onClick={() => setViewMode("home")}
          aria-label="flshy home"
          className={`select-none rounded-[6px] font-display text-[30px] leading-none tracking-tight text-white ${FOCUS_RING}`}
        >
          flshy
        </button>

        <nav className="flex items-center gap-1" aria-label="Dashboard">
          {NAV_ITEMS.map((item) => {
            const isActive = item.id === viewMode;
            return (
              <button
                key={item.id}
                type="button"
                aria-current={isActive ? "page" : undefined}
                onClick={() => setViewMode(item.id)}
                className={`rounded-[8px] px-3 py-1.5 text-[14px] transition-colors ${FOCUS_RING} ${
                  isActive
                    ? "bg-white/[0.08] text-white"
                    : `${MUTED} hover:text-white`
                }`}
              >
                {item.label}
              </button>
            );
          })}
        </nav>

        <AccountMenu align="end" placement="below" variant="surface" />
      </header>

      <main className="relative flex-1">
        {viewMode === "home" && renderHome()}
        {viewMode === "templates" &&
          renderList(
            "Templates",
            "Start from a ready made scene and make it yours.",
            <TemplateGallery onTemplateApplied={handleTemplateApplied} />,
          )}
        {viewMode === "projects" &&
          renderList(
            "Projects",
            "Everything you have worked on, saved in this browser.",
            <RecentProjects onProjectSelected={handleProjectSelected} />,
          )}
      </main>

      <input
        ref={fileInputRef}
        type="file"
        accept="video/*,audio/*,image/*"
        multiple
        className="hidden"
        onChange={handleFileInputChange}
      />
    </div>
  );
};

export default WelcomeScreen;
